import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { ShiftPattern } from '@/types/schedule.types';

interface ShiftScheduleFormProps {
  pattern: ShiftPattern;
  onChange: (pattern: ShiftPattern) => void;
  disabled?: boolean;
}

// Popular shift presets
const SHIFT_PRESETS = [
  { label: '2/2', work_days: 2, off_days: 2 },
  { label: '3/3', work_days: 3, off_days: 3 },
  { label: '4/4', work_days: 4, off_days: 4 },
  { label: '2/1', work_days: 2, off_days: 1 },
  { label: '3/1', work_days: 3, off_days: 1 },
  { label: '5/2', work_days: 5, off_days: 2 },
];

export const ShiftScheduleForm: React.FC<ShiftScheduleFormProps> = ({
  pattern,
  onChange,
  disabled = false,
}) => {
  const updatePattern = (updates: Partial<ShiftPattern>) => {
    onChange({ ...pattern, ...updates });
  };

  const applyPreset = (work_days: number, off_days: number) => {
    updatePattern({ work_days, off_days });
  };

  // Generate preview pattern (show 16 days)
  const generatePreview = () => {
    const cycleLength = pattern.work_days + pattern.off_days;
    if (cycleLength === 0) return [];

    const preview = [];
    for (let i = 0; i < 16; i++) {
      const positionInCycle = i % cycleLength;
      const isWorking = positionInCycle < pattern.work_days;
      preview.push({
        day: i + 1,
        isWorking,
        label: isWorking ? 'Раб' : 'Вых',
      });
    }
    return preview;
  };

  const preview = generatePreview();

  return (
    <div className="space-y-6">
      {/* Quick presets */}
      <div className="space-y-2">
        <Label>Быстрый выбор</Label>
        <div className="flex flex-wrap gap-2">
          {SHIFT_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPreset(preset.work_days, preset.off_days)}
              disabled={disabled}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Pattern configuration */}
      <div className="rounded-lg border p-4 space-y-4 bg-card">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Паттерн смены
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="work_days">
              Рабочие дни <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="work_days"
                type="number"
                min="1"
                max="30"
                value={pattern.work_days}
                onChange={(e) =>
                  updatePattern({ work_days: parseInt(e.target.value) || 1 })
                }
                disabled={disabled}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">дн. подряд</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="off_days">
              Выходные дни <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="off_days"
                type="number"
                min="1"
                max="30"
                value={pattern.off_days}
                onChange={(e) =>
                  updatePattern({ off_days: parseInt(e.target.value) || 1 })
                }
                disabled={disabled}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">дн. подряд</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Рабочее время <span className="text-destructive">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Input
              type="time"
              value={pattern.work_time.start}
              onChange={(e) =>
                updatePattern({
                  work_time: { ...pattern.work_time, start: e.target.value },
                })
              }
              disabled={disabled}
              className="w-32"
            />
            <span className="text-muted-foreground">—</span>
            <Input
              type="time"
              value={pattern.work_time.end}
              onChange={(e) =>
                updatePattern({
                  work_time: { ...pattern.work_time, end: e.target.value },
                })
              }
              disabled={disabled}
              className="w-32"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="cycle_start_date">
            Дата начала цикла <span className="text-destructive">*</span>
          </Label>
          <Input
            id="cycle_start_date"
            type="date"
            value={pattern.cycle_start_date}
            onChange={(e) => updatePattern({ cycle_start_date: e.target.value })}
            disabled={disabled}
            className="w-48"
          />
          <p className="text-xs text-muted-foreground">
            💡 Укажите дату, с которой начинается цикл (например, первый рабочий день)
          </p>
        </div>
      </div>

      {/* Preview */}
      {preview.length > 0 && (
        <div className="rounded-lg border p-4 bg-muted/30 space-y-3">
          <h4 className="text-sm font-semibold">📊 Превью паттерна</h4>
          <div className="flex flex-wrap gap-2">
            {preview.map((day) => (
              <div
                key={day.day}
                className={`flex flex-col items-center justify-center w-12 h-12 rounded border text-xs font-medium ${
                  day.isWorking
                    ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200'
                    : 'bg-gray-100 border-gray-300 text-gray-600 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400'
                }`}
              >
                <span className="text-[10px] text-muted-foreground">Д{day.day}</span>
                <span>{day.label}</span>
              </div>
            ))}
            <div className="flex items-center text-muted-foreground text-xs">...</div>
          </div>
          <p className="text-xs text-muted-foreground">
            Цикл: {pattern.work_days} раб. + {pattern.off_days} вых. ={' '}
            {pattern.work_days + pattern.off_days} дней (повторяется)
          </p>
        </div>
      )}
    </div>
  );
};
